import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileSearch,
  Shield,
  CheckCircle,
  AlertTriangle,
  Clock,
  Link as LinkIcon,
  Hash,
  Eye,
  Download,
  RefreshCw,
  Lock,
  Users,
  FileText,
  Activity,
  Sparkles,
  Calendar
} from 'lucide-react';
import AIAssistantModal from '../components/AIAssistantModal';
import SecondaryAuth from '../components/SecondaryAuth';

const AuditVaultContent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trail');
  const [auditTrail, setAuditTrail] = useState([]);
  const [integrityStatus, setIntegrityStatus] = useState({ verified: true, entries: 24, lastCheck: 'Just now' });
  
  // AI Audit Summary State
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);
  const [summaryError, setSummaryError] = useState(null);

  useEffect(() => {
    fetchData();
    logAccess();
  }, []);

  const logAccess = async () => {
    // Silently ignore audit logging errors (apiFetch never throws for HTTP errors)
    await apiFetch(`${API}/audit/log-access`, {
      method: 'POST',
      body: {
        dashboard_id: 'auditor',
        dashboard_name: 'Audit Vault',
        action: 'dashboard_view',
        timestamp: new Date().toISOString()
      }
    });
  };

  const fetchData = async () => {
    const trailData = await apiFetch(`${API}/audit/trail`);
    setAuditTrail(trailData?._error ? [] : (trailData?.audit_entries || []));
    setLoading(false);
  };

  // Sample audit trail entries
  const sampleAuditEntries = [
    { id: 'AUD-001', timestamp: '2025-01-26 14:32:15', user: 'elena.rodriguez@company.com', action: 'DATA_UPLOAD', entity: 'emissions_data_2024.csv', details: 'Uploaded 10 records', hash: 'a3f2c8d9...' },
    { id: 'AUD-002', timestamp: '2025-01-26 14:28:42', user: 'marcus.johnson@company.com', action: 'CALCULATION', entity: 'Scope 1 GHG Total', details: 'Calculated quarterly emissions', hash: 'b7e4f1a2...' },
    { id: 'AUD-003', timestamp: '2025-01-26 12:15:33', user: 'sarah.chen@company.com', action: 'REPORT_EXPORT', entity: 'CSRD Annual Report', details: 'Generated PDF report', hash: 'c9d2e5b8...' },
    { id: 'AUD-004', timestamp: '2025-01-25 16:45:21', user: 'elena.rodriguez@company.com', action: 'DATA_UPDATE', entity: 'workforce_metrics', details: 'Updated Q4 headcount', hash: 'd1f3a7c4...' },
    { id: 'AUD-005', timestamp: '2025-01-25 11:20:08', user: 'system', action: 'INTEGRITY_CHECK', entity: 'Chain Verification', details: 'Automated daily check passed', hash: 'e5g8h2j6...' }
  ];

  // Data lineage sample
  const lineageNodes = [
    { id: 'source', type: 'source', label: 'Raw Data Upload', details: 'emissions_data_2024.csv' },
    { id: 'transform1', type: 'transform', label: 'Data Validation', details: 'Schema check, null handling' },
    { id: 'transform2', type: 'transform', label: 'Unit Conversion', details: 'kWh → MWh, kg → tonnes' },
    { id: 'calc', type: 'calculation', label: 'GHG Calculation', details: 'GHG Protocol methodology' },
    { id: 'output', type: 'output', label: 'Final Metric', details: 'Scope 1: 9,376 tCO2e' }
  ];

  // Access log sample
  const accessLogs = [
    { timestamp: '2025-01-26 14:35:22', user: 'james.park@company.com', action: 'VIEW', resource: 'Audit Vault', ip: '192.168.1.105' },
    { timestamp: '2025-01-26 14:32:15', user: 'elena.rodriguez@company.com', action: 'UPLOAD', resource: 'Data Lab', ip: '192.168.1.112' },
    { timestamp: '2025-01-26 13:45:08', user: 'marcus.johnson@company.com', action: 'VIEW', resource: 'Compliance Console', ip: '192.168.1.108' },
    { timestamp: '2025-01-26 12:15:33', user: 'sarah.chen@company.com', action: 'EXPORT', resource: 'Executive Hub', ip: '192.168.1.101' }
  ];

  const actionColors = {
    DATA_UPLOAD: 'bg-purple-100 text-purple-700',
    CALCULATION: 'bg-blue-100 text-blue-700',
    REPORT_EXPORT: 'bg-emerald-100 text-emerald-700',
    DATA_UPDATE: 'bg-amber-100 text-amber-700',
    INTEGRITY_CHECK: 'bg-slate-100 text-slate-700',
    VIEW: 'bg-slate-100 text-slate-600',
    UPLOAD: 'bg-purple-100 text-purple-700',
    EXPORT: 'bg-emerald-100 text-emerald-700'
  };

  // Generate Audit Period Summary with AI
  const generateAuditSummary = async () => {
    setShowSummaryModal(true);
    setSummaryLoading(true);
    setSummaryError(null);
    setSummaryResult(null);
    
    // Calculate date range (last 30 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    
    const data = await apiFetch(`${API}/ai/auditor/summarize-period`, {
      method: 'POST',
      body: {
        period_start: startDate.toISOString().split('T')[0],
        period_end: endDate.toISOString().split('T')[0],
        focus_areas: ['data_uploads', 'calculations', 'exports']
      }
    });
    if (data?._error) {
      setSummaryError(data.message);
    } else {
      setSummaryResult(data);
    }
    setSummaryLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 rounded-lg bg-amber-600 flex items-center justify-center animate-pulse">
          <FileSearch size={24} className="text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-vault">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard-selector')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Audit Vault
            </h1>
            <p className="text-slate-600">Immutable audit trails and integrity verification</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIntegrityStatus({ ...integrityStatus, lastCheck: 'Just now' })}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            <Shield size={16} />
            Verify Integrity
          </button>
          <button 
            onClick={generateAuditSummary}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700"
            data-testid="summarize-audit-btn"
          >
            <Sparkles size={16} />
            AI Summary
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* Integrity Status Banner */}
      <div className={`p-4 rounded-xl flex items-center justify-between ${integrityStatus.verified ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex items-center gap-4">
          {integrityStatus.verified ? (
            <CheckCircle size={24} className="text-emerald-600" />
          ) : (
            <AlertTriangle size={24} className="text-red-600" />
          )}
          <div>
            <p className={`font-semibold ${integrityStatus.verified ? 'text-emerald-900' : 'text-red-900'}`}>
              {integrityStatus.verified ? 'Chain Integrity Verified' : 'Integrity Check Failed'}
            </p>
            <p className={`text-sm ${integrityStatus.verified ? 'text-emerald-700' : 'text-red-700'}`}>
              {integrityStatus.entries} entries verified • Last check: {integrityStatus.lastCheck}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Hash size={16} className="text-slate-400" />
          <span className="font-mono text-slate-600">SHA-256 Cryptographic Hashing</span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Activity size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Audit Entries</p>
              <p className="text-2xl font-bold text-slate-900">24</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Integrity</p>
              <p className="text-2xl font-bold text-emerald-600">100%</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <LinkIcon size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Lineage Chains</p>
              <p className="text-2xl font-bold text-slate-900">5</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Users size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Active Users</p>
              <p className="text-2xl font-bold text-slate-900">4</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'trail', label: 'Audit Trail', icon: FileSearch },
          { id: 'lineage', label: 'Data Lineage', icon: LinkIcon },
          { id: 'integrity', label: 'Integrity Proofs', icon: Shield },
          { id: 'access', label: 'Access Logs', icon: Lock }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'text-amber-600 border-amber-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Audit Trail Tab */}
      {activeTab === 'trail' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Immutable Audit Trail</h3>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Clock size={14} />
              Real-time logging enabled
            </div>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Entity</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Hash</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Verify</th>
              </tr>
            </thead>
            <tbody>
              {sampleAuditEntries.map((entry) => (
                <tr key={entry.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-sm text-slate-900">{entry.id}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{entry.timestamp}</td>
                  <td className="px-4 py-3 text-sm text-slate-900">{entry.user}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${actionColors[entry.action]}`}>
                      {entry.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{entry.entity}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{entry.hash}</td>
                  <td className="px-4 py-3">
                    <button className="text-amber-600 hover:text-amber-800">
                      <CheckCircle size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Data Lineage Tab */}
      {activeTab === 'lineage' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-slate-900 mb-6">Data Lineage: GHG Emissions Calculation</h3>
          <div className="flex items-center justify-between">
            {lineageNodes.map((node, idx) => (
              <React.Fragment key={node.id}>
                <div className={`flex-1 max-w-48 p-4 rounded-xl border-2 ${
                  node.type === 'source' ? 'bg-purple-50 border-purple-200' :
                  node.type === 'transform' ? 'bg-blue-50 border-blue-200' :
                  node.type === 'calculation' ? 'bg-amber-50 border-amber-200' :
                  'bg-emerald-50 border-emerald-200'
                }`}>
                  <p className={`text-xs font-semibold uppercase mb-1 ${
                    node.type === 'source' ? 'text-purple-600' :
                    node.type === 'transform' ? 'text-blue-600' :
                    node.type === 'calculation' ? 'text-amber-600' :
                    'text-emerald-600'
                  }`}>{node.type}</p>
                  <p className="text-sm font-medium text-slate-900">{node.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{node.details}</p>
                </div>
                {idx < lineageNodes.length - 1 && (
                  <div className="flex-shrink-0 px-2">
                    <div className="w-8 h-0.5 bg-slate-300" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-8 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Lineage Summary:</strong> This metric traces from raw data upload through 2 transformations and 1 calculation step.
              All transformations are documented and reversible. Total processing time: 2.3 seconds.
            </p>
          </div>
        </div>
      )}

      {/* Integrity Proofs Tab */}
      {activeTab === 'integrity' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Cryptographic Integrity Verification</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Current Hash</p>
                <p className="font-mono text-sm text-slate-900 break-all">
                  a3f2c8d9e7b4a1f6c2d8e5b3a9f7c4d2e1b8a5f3c7d9e2b4a6f1c3d5e8b2a4f6
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Previous Hash</p>
                <p className="font-mono text-sm text-slate-900 break-all">
                  b7e4f1a2c9d6e3b8a5f2c7d4e1b9a6f3c8d5e2b7a4f1c6d9e3b5a8f2c4d7e1b3
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-emerald-600">
              <CheckCircle size={16} />
              Chain integrity verified - No tampering detected
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Verification History</h3>
            <div className="space-y-3">
              {[
                { time: 'Just now', result: 'Pass', entries: 24 },
                { time: '1 hour ago', result: 'Pass', entries: 23 },
                { time: '6 hours ago', result: 'Pass', entries: 21 },
                { time: '24 hours ago', result: 'Pass', entries: 18 }
              ].map((check, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-emerald-600" />
                    <span className="text-sm text-slate-900">{check.time}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-500">{check.entries} entries</span>
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">{check.result}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Access Logs Tab */}
      {activeTab === 'access' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Dashboard Access Logs</h3>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Resource</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">IP Address</th>
              </tr>
            </thead>
            <tbody>
              {accessLogs.map((log, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-sm text-slate-600">{log.timestamp}</td>
                  <td className="px-4 py-3 text-sm text-slate-900">{log.user}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${actionColors[log.action]}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{log.resource}</td>
                  <td className="px-4 py-3 font-mono text-sm text-slate-500">{log.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AI Audit Summary Modal */}
      <AIAssistantModal
        isOpen={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        title="AI Audit Period Summary"
        icon={Calendar}
        iconColor="text-amber-600"
        iconBg="bg-amber-100"
        loading={summaryLoading}
        result={summaryResult}
        error={summaryError}
        onRetry={generateAuditSummary}
      />
    </div>
  );
};

/**
 * AuditVault - Wrapped with Secondary Authentication
 * This dashboard contains sensitive audit trail data
 */
const AuditVault = () => {
  return (
    <SecondaryAuth 
      dashboardName="Audit Vault"
      securityLevel="high"
    >
      <AuditVaultContent />
    </SecondaryAuth>
  );
};

export default AuditVault;
